import { FundingMethodEnum } from '@interfaces/open-account/enums';
import { Request, Response } from 'express';
import * as core from 'express-serve-static-core';

export enum ProcessEndStateCodeEnum {
  LiteReg = 'LTWR',
  Esign = 'ESGN',
  KickToLegacy = 'KTL',
  BrowserClose = 'CNCL',
  KickToPdf = 'PRLC',
}

export enum ProcessPageTypeCodeEnum {
  COOFlowTypeCode = 'COO',
  OverviewPage = 'OVPG',
  AccountSelection = 'TYAS',
  PersonalInfo = 'TYPI',
  ContactInfo = 'TYCI',
  UsernamePassword = 'WRUP',
  SecQuestions = 'WRSQ',
  LoginPage = 'LGIN',
  BankOnFile = 'BAOF',
  FundingMethod = 'EFFM',
  InitialContribution = 'ESCA',
  Employment = 'TYES',
  SourceOfFunds = 'CSRC',
  InvestmentGoals = 'CIGL',
  IncomeNetWorth = 'CINW',
  Review = 'REVW',
  Esign = 'RESB',
  Confirm = 'CNFP',
  Empty = '',
}

interface InheritanceStartDetails {
  processPageType: ProcessPageTypeCodeEnum;
  newOaUniqueStart: boolean;
  flowType: ProcessPageTypeCodeEnum;
  segmentType: string;
}

interface FlowStartDetails {
  flowName: string;
  transferType: string;
  multiSigner?: boolean;
}

interface NewAccountStartDetails {
  processPageType: ProcessPageTypeCodeEnum;
  newOaUniqueStart: boolean;
  flowType: ProcessPageTypeCodeEnum;
  segmentType: string;
}

interface SuccessfulSubmitDetails {
  processPageType: ProcessPageTypeCodeEnum;
  processEndState: ProcessEndStateCodeEnum;
  confirmationNumber: string;
  newOaUniqueStart: boolean;
  paymentMethod: FundingMethodEnum;
  accountType: string;
  newAccount: boolean;
  expectedDollarAmount: string;
  s24RuleFailId?: string | object;
  flowType: ProcessPageTypeCodeEnum;
  segmentType: string;
}

interface FlowSubmitDetails {
  flowName: string;
  transferType: string;
  stpProcessingId?: string;
  appianCaseId?: string;
  dollarAmount?: string;
  fromRegistrationType: string;
  toRegistrationType: string;
  fromAccountNumber?: string;
  toAccountNumber?: string;
  isCharityOrganization?: boolean;
  isInternalGift?: boolean;
  multiSigner?: boolean;
  confirmationNumber?: string;
  newAccountFlag?: boolean;
  decedentPoid?: string;
  costBasisOptIn?: string;
}

interface TrackFailureDetails {
  processPageType: ProcessPageTypeCodeEnum;
  processEndState: ProcessEndStateCodeEnum;
  newOaUniqueStart: boolean;
  accountType: string;
  flowType: ProcessPageTypeCodeEnum;
  segmentType: string;
}

interface BaseMetricsRequest {
  // There are many journey names, but we are change-of-ownership so we can narrow set of types
  journeyName: 'change-of-ownership';
  // There are many communication channels, but we are web so we can narrow set of types
  communicationChannel: 'web';
  // We are on version 2.0, can either add or replace depending if we partially or fully migrate to next version
  version: '1.0' | '2.0';
}

export interface InheritanceStartMetricsRequest extends BaseMetricsRequest {
  details: InheritanceStartDetails;
  eventName: 'initial-start';
  correlationId?: string;
}

export interface FlowStartMetricsRequest extends BaseMetricsRequest {
  details: FlowStartDetails;
  eventName: 'flow-started';
}

export interface FlowSubmitMetricsRequest extends BaseMetricsRequest {
  details: FlowSubmitDetails;
  eventName: 'flow-submitted';
  correlationId: string;
}

export interface NewAccountStartMetricsRequest extends BaseMetricsRequest {
  details: NewAccountStartDetails;
  eventName: 'new-account-start';
  correlationId?: string;
}

export interface SuccessfulSubmitMetricsRequest extends BaseMetricsRequest {
  details: SuccessfulSubmitDetails;
  eventName: 'successful-submit';
  correlationId: string;
}

export interface TrackFailureMetricsRequest extends BaseMetricsRequest {
  details: TrackFailureDetails;
  eventName: 'track-failure';
  correlationId: string;
}

export type IObeSubmitMetricsRequest =
  | FlowStartMetricsRequest
  | FlowSubmitMetricsRequest
  | InheritanceStartMetricsRequest
  | NewAccountStartMetricsRequest
  | SuccessfulSubmitMetricsRequest
  | TrackFailureMetricsRequest;

export interface IMetricsTrackingData {
  correlationId: string;
}

export interface SubmitEventSuccessResponse {
  correlationId: string;
}

export type SubmitMetricsResponse = IMetricsTrackingData | undefined;

export type SubmitMetricsMiddlewareRequest = Request<
  core.ParamsDictionary,
  IObeSubmitMetricsRequest
>;

export type SubmitMetricsMiddlewareResponse = Response<SubmitMetricsResponse>;
